import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import linesGeoJSON from './data/lines.json';
import {
  MAP_CENTER,
  MAP_ZOOM,
  MAP_STYLE_LIGHT,
  MAP_STYLE_DARK,
  LINE_COLORS,
} from './data/stations-meta';
import type { Station, TrainPosition } from './api';
import { getStationLineCodes } from './api';

let map: maplibregl.Map;
let trainMarkers: Map<string, maplibregl.Marker> = new Map();
let activeStationCode: string | null = null;

type StationClickHandler = (stationCode: string, station: Station) => void;
let onStationClick: StationClickHandler | null = null;

// Station lookup populated after addStations
let stationLookup: Map<string, Station> = new Map();

export function isDarkMode(): boolean {
  const theme = document.documentElement.getAttribute('data-theme');
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function initMap(): maplibregl.Map {
  const style = isDarkMode() ? MAP_STYLE_DARK : MAP_STYLE_LIGHT;

  map = new maplibregl.Map({
    container: 'map',
    style,
    center: MAP_CENTER,
    zoom: MAP_ZOOM,
    minZoom: 9,
    maxZoom: 17,
    pitch: 0,
    attributionControl: false,
  });

  map.addControl(
    new maplibregl.AttributionControl({ compact: true }),
    'bottom-right'
  );

  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');

  map.on('load', () => {
    addMetroLines();
  });

  map.on('click', 'stations-layer', (e) => {
    const feat = e.features?.[0];
    if (!feat) return;
    const code = feat.properties?.code as string;
    const station = stationLookup.get(code);
    if (station) {
      setActiveStation(code);
      onStationClick?.(code, station);
    }
  });

  map.getCanvas().style.cursor = '';
  map.on('mouseenter', 'stations-layer', () => {
    map.getCanvas().style.cursor = 'pointer';
  });
  map.on('mouseleave', 'stations-layer', () => {
    map.getCanvas().style.cursor = '';
  });

  return map;
}

export function getMap(): maplibregl.Map {
  return map;
}

export function setStationClickHandler(handler: StationClickHandler) {
  onStationClick = handler;
}

function addMetroLines() {
  map.addSource('metro-lines', {
    type: 'geojson',
    data: linesGeoJSON as GeoJSON.FeatureCollection,
  });

  const lineOrder = ['SV', 'YL', 'GR', 'BL', 'OR', 'RD'];
  const features = (linesGeoJSON as GeoJSON.FeatureCollection).features;

  lineOrder.forEach((lineAbbr) => {
    const feature = features.find(
      (f) => f.properties?.line === lineNameForAbbr(lineAbbr)
    );
    if (!feature) return;

    const sourceId = `line-${lineAbbr}`;
    map.addSource(sourceId, {
      type: 'geojson',
      data: feature as GeoJSON.Feature,
    });

    // Outer glow layer
    map.addLayer({
      id: `${sourceId}-glow`,
      type: 'line',
      source: sourceId,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': LINE_COLORS[lineAbbr],
        'line-width': 8,
        'line-opacity': 0.15,
        'line-blur': 4,
      },
    });

    // Main line layer
    map.addLayer({
      id: `${sourceId}-main`,
      type: 'line',
      source: sourceId,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': LINE_COLORS[lineAbbr],
        'line-width': ['interpolate', ['linear'], ['zoom'], 9, 2, 13, 4, 17, 6],
        'line-opacity': 0.9,
      },
    });
  });
}

function lineNameForAbbr(abbr: string): string {
  const map: Record<string, string> = {
    RD: 'Red', OR: 'Orange', YL: 'Yellow',
    GR: 'Green', BL: 'Blue', SV: 'Silver',
  };
  return map[abbr] || abbr;
}

export function addStations(stations: Station[]) {
  stationLookup.clear();
  stations.forEach((s) => stationLookup.set(s.Code, s));
  addStationsLayer();
}

function addStationsLayer() {
  if (stationLookup.size === 0) return;
  if (map.getSource('stations')) {
    map.setFilter('stations-active-layer', activeStationCode ? ['==', ['get', 'code'], activeStationCode] : ['==', ['get', 'code'], '']);
    return;
  }

  const features: GeoJSON.Feature<GeoJSON.Point, { code: string; transfer: number }>[] = [];
  stationLookup.forEach((s) => {
    const lineCodes = getStationLineCodes(s);
    const isTransfer = lineCodes.length > 1 || s.StationTogether1 !== '' ? 1 : 0;
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [s.Lon, s.Lat] },
      properties: { code: s.Code, transfer: isTransfer },
    });
  });

  const geojson: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features };

  map.addSource('stations', { type: 'geojson', data: geojson });

  map.addLayer({
    id: 'stations-layer',
    type: 'circle',
    source: 'stations',
    paint: {
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        9, 3,
        11, 5,
        13, 8,
        15, 12,
        17, 16,
      ],
      'circle-color': '#1d1d1f',
      'circle-stroke-width': [
        'interpolate',
        ['linear'],
        ['zoom'],
        9, 1.5,
        13, 2.5,
        17, 3.5,
      ],
      'circle-stroke-color': '#ffffff',
    },
  });

  map.addLayer({
    id: 'stations-active-layer',
    type: 'circle',
    source: 'stations',
    filter: activeStationCode ? ['==', ['get', 'code'], activeStationCode] : ['==', ['get', 'code'], ''],
    paint: {
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        9, 5,
        11, 8,
        13, 12,
        15, 18,
        17, 24,
      ],
      'circle-color': 'transparent',
      'circle-stroke-width': 3,
      'circle-stroke-color': '#0077C8',
    },
  });
}

export function setActiveStation(code: string | null) {
  activeStationCode = code;

  const layer = map.getLayer('stations-active-layer');
  if (layer) {
    map.setFilter('stations-active-layer', code ? ['==', ['get', 'code'], code] : ['==', ['get', 'code'], '']);
  }

  if (code) {
    const station = stationLookup.get(code);
    if (station) {
      map.flyTo({
        center: [station.Lon, station.Lat],
        zoom: Math.max(map.getZoom(), 13),
        duration: 800,
        essential: true,
      });
    }
  }
}

export function clearActiveStation() {
  if (activeStationCode) {
    const layer = map.getLayer('stations-active-layer');
    if (layer) {
      map.setFilter('stations-active-layer', ['==', ['get', 'code'], '']);
    }
    activeStationCode = null;
  }
}

export function getStationByCode(code: string): Station | undefined {
  return stationLookup.get(code);
}

export function updateTrainPositions(trains: TrainPosition[]) {
  const currentIds = new Set(trains.map((t) => t.TrainId));

  // Remove old markers
  trainMarkers.forEach((marker, id) => {
    if (!currentIds.has(id)) {
      marker.remove();
      trainMarkers.delete(id);
    }
  });

  // Train positions from WMATA are circuit-based, not lat/lon.
  // We can't plot exact positions without a circuit-to-coordinate mapping.
  // Instead, we'll show a count in the stats bar.
  // If we had circuit data, we'd update marker positions here.
}

export function switchMapStyle(dark: boolean) {
  const style = dark ? MAP_STYLE_DARK : MAP_STYLE_LIGHT;

  map.setStyle(style);

  map.once('style.load', () => {
    addMetroLines();
    addStationsLayer();
  });
}

export function flyToStation(code: string) {
  const station = stationLookup.get(code);
  if (!station) return;

  map.flyTo({
    center: [station.Lon, station.Lat],
    zoom: 15,
    duration: 1200,
    essential: true,
  });

  setActiveStation(code);
}

let userLocationMarker: maplibregl.Marker | null = null;

export function flyToUserLocation(
  onSuccess?: (nearestStation: Station | null) => void,
  onError?: (err: Error) => void
) {
  if (!navigator.geolocation) {
    onError?.(new Error('Geolocation is not supported'));
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const { longitude, latitude } = pos.coords;

      map.flyTo({
        center: [longitude, latitude],
        zoom: 15,
        duration: 1000,
        essential: true,
      });

      // Add or update user location marker
      if (userLocationMarker) userLocationMarker.remove();

      const el = document.createElement('div');
      el.className = 'user-location-marker';
      el.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <circle cx="12" cy="12" r="4" fill="currentColor"/>
        </svg>
      `;

      userLocationMarker = new maplibregl.Marker({ element: el })
        .setLngLat([longitude, latitude])
        .addTo(map);

      const nearest = findNearestStation(latitude, longitude);
      onSuccess?.(nearest);
    },
    (err) => onError?.(new Error(err.message)),
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
  );
}

export function findNearestStation(lat: number, lon: number): Station | null {
  const stations = Array.from(stationLookup.values());
  if (stations.length === 0) return null;

  let nearest: Station | null = null;
  let minDist = Infinity;

  for (const s of stations) {
    const dist = haversineKm(lat, lon, s.Lat, s.Lon);
    if (dist < minDist) {
      minDist = dist;
      nearest = s;
    }
  }

  return nearest;
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
